import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { isToday, parseISO } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";

import NewTicketModal from "../NewTicketModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.boxticket,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    padding: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      border: "none",
    },
  },
  stack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  },
  title: {
    margin: 0,
    fontSize: "1.15rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 120px))",
    gap: 12,
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: "14px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    cursor: "pointer",
    transition: "border-color .15s",
    "&:hover": { borderColor: theme.palette.primary.main },
  },
  cardStatic: { cursor: "default", "&:hover": { borderColor: theme.palette.divider } },
  number: {
    fontSize: "1.5rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.primary,
  },
  label: { fontSize: "0.75rem", color: theme.palette.text.secondary },
  button: { borderRadius: 999, textTransform: "none", fontWeight: 600, padding: "8px 20px" },
  hint: { fontSize: "0.8rem", color: theme.palette.text.secondary, margin: 0 },
}));

// Símbolo da marca (os dois balões), o mesmo dos arquivos duo-*.svg
const BrandSymbol = ({ size = 84 }) => (
  <svg viewBox="0 0 96 90" width={size} aria-hidden="true">
    <path d="M22,8 L46,8 L46,64 L30,64 L16,80 Q12,84 14,76 L16,64 L22,64 Q10,64 10,52 L10,20 Q10,8 22,8 Z" fill="#F08A3C" />
    <path d="M50,8 L74,8 Q86,8 86,20 L86,52 Q86,64 74,64 L50,64 Z" fill="#2D7BE8" />
  </svg>
);

// Conta os tickets resolvidos hoje pelo usuário. A API ordena por updatedAt
// (mais recente primeiro), então basta paginar enquanto o último item for de hoje.
const countClosedToday = async () => {
  let total = 0;
  for (let page = 1; page <= 5; page++) {
    const { data } = await api.get("/tickets", {
      params: { status: "closed", showAll: "false", pageNumber: page },
    });
    const tickets = data?.tickets || [];
    const today = tickets.filter((t) => t.updatedAt && isToday(parseISO(t.updatedAt)));
    total += today.length;
    if (!data?.hasMore || today.length < tickets.length) break;
  }
  return total;
};

const TicketsEmptyState = ({
  openCount = 0,
  pendingCount = 0,
  onSelectTab,
  hint = "Selecione um chat na lista ao lado para começar a conversar.",
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [closedToday, setClosedToday] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let active = true;
    countClosedToday()
      .then((n) => active && setClosedToday(n))
      .catch(() => active && setClosedToday(null));
    return () => { active = false; };
  }, []);

  const firstName = (user?.name || "").split(" ")[0];

  const handleCloseModal = (ticket) => {
    setModalOpen(false);
    if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
  };

  return (
    <div className={classes.root}>
      <NewTicketModal modalOpen={modalOpen} onClose={handleCloseModal} />
      <div className={classes.stack}>
        <BrandSymbol />
        <p className={classes.title}>
          {firstName ? `Olá, ${firstName}! Nenhum chat aberto.` : "Nenhum chat aberto."}
        </p>
        <div className={classes.cards}>
          <div className={classes.card} onClick={() => onSelectTab && onSelectTab("pending")}>
            <span className={classes.number} style={{ color: "#F08A3C" }}>{pendingCount}</span>
            <span className={classes.label}>Aguardando</span>
          </div>
          <div className={classes.card} onClick={() => onSelectTab && onSelectTab("open")}>
            <span className={classes.number} style={{ color: "#2D7BE8" }}>{openCount}</span>
            <span className={classes.label}>Atendendo</span>
          </div>
          <div className={`${classes.card} ${classes.cardStatic}`}>
            <span className={classes.number}>{closedToday === null ? "–" : closedToday}</span>
            <span className={classes.label}>Resolvidos hoje</span>
          </div>
        </div>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<AddIcon />}
          className={classes.button}
          onClick={() => setModalOpen(true)}
        >
          Novo atendimento
        </Button>
        <p className={classes.hint}>{hint}</p>
      </div>
    </div>
  );
};

export default TicketsEmptyState;
